import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Store, Sparkles } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import GoldenCone from "../components/GoldenCone";

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const COMPARISONS = [
  { stat: "−60%", title: "Less Fat", desc: "Gelato contains roughly 60% less fat than American ice cream — lighter, yet creamier on the palate." },
  { stat: "−40%", title: "Less Air", desc: "Churned slowly, gelato traps about 40% less air. The result: a denser, silkier scoop." },
  { stat: "+10°", title: "Warmer Serve", desc: "Served around 10°F warmer, gelato unleashes flavor instead of numbing it." },
  { stat: "100%", title: "Natural", desc: "Made from scratch — no industrial mixes, no shortcuts, no compromise." },
];

export default function Home() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#B8C5CD" }}>
      <Header />

      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-12 md:pt-20 pb-20 md:pb-32 grid md:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <div className="eyebrow mb-6">Santa Chiara · Alta Pasticceria</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight" style={{ color: "#2C2A28" }}>
              Create the gelato<br />
              <em className="font-light italic" style={{ color: "#7B1E3B" }}>nobody</em> has ever made <span style={{ color: "#B89968" }}>for you</span>.
            </h1>
            <p className="mt-7 text-base md:text-lg max-w-xl leading-relaxed" style={{ color: "#2C2A28" }}>
              Any flavor. Any dream. Hand-crafted in our atelier from natural ingredients — never from industrial mixes.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/create"
                data-testid="hero-create-cta"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full font-medium transition-all duration-300"
                style={{ backgroundColor: "#2C2A28", color: "#F0E6D2" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#B89968")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2C2A28")}
              >
                Create Your Gelato
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a href="#educate" className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: "#2C2A28" }}>
                Why gelato? <ArrowRight size={14} />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex items-center justify-center"
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <GoldenCone size={320} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* EDUCATIONAL SECTION */}
      <section id="educate" data-testid="education-section" className="py-20 lg:py-28" style={{ backgroundColor: "#C5D0D6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="max-w-3xl">
            <div className="eyebrow mb-4">Gelato vs Ice Cream</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight" style={{ color: "#2C2A28" }}>
              Why Italian gelato is in a class of its own.
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: "#2C2A28" }}>
              Gelato is not "Italian ice cream." It is a different craft — older, denser, more honest. Less fat. Less air. Served warmer so flavors bloom. It tastes the way nature intended.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {COMPARISONS.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`comparison-card-${i}`}
                className="bg-white rounded-2xl p-7 border"
                style={{ borderColor: "rgba(184,153,104,0.25)", boxShadow: "0 8px 30px rgba(0,0,0,0.04)" }}
              >
                <div className="font-serif-display text-5xl mb-3" style={{ color: "#B89968" }}>{c.stat}</div>
                <div className="font-serif-display text-xl mb-2" style={{ color: "#2C2A28" }}>{c.title}</div>
                <p className="text-sm leading-relaxed" style={{ color: "#4A4744" }}>{c.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-16 max-w-4xl">
            <div className="rounded-3xl p-8 md:p-12 border-l-4" style={{ backgroundColor: "#B8C5CD", borderColor: "#B89968" }}>
              <p className="font-serif-display text-2xl md:text-3xl leading-snug italic" style={{ color: "#2C2A28" }}>
                "Our gelato is made naturally, from scratch, without industrial mixes or shortcuts. That is why we can create any gelato, any flavor, any dream."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* EMOTIONAL PROMO */}
      <section data-testid="promo-section" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeUp} className="order-2 md:order-1">
            <div className="eyebrow mb-4">Your imagination, churned into gelato</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight" style={{ color: "#2C2A28" }}>
              Tired of the<br/> same old flavors?
            </h2>
            <div className="mt-6 space-y-5 text-base md:text-lg leading-relaxed" style={{ color: "#2C2A28" }}>
              <p>Have you always dreamed of a flavor nobody ever created for you?</p>
              <p>Do you want to surprise your guests at a dinner, party, or private event?</p>
              <p>Are you a restaurant owner looking for an exclusive gelato flavor created only for your menu?</p>
              <p className="font-serif-display italic text-2xl pt-2" style={{ color: "#7B1E3B" }}>
                At Santa Chiara, your imagination becomes gelato.
              </p>
            </div>
            <Link
              to="/create"
              data-testid="promo-create-cta"
              className="mt-10 inline-flex items-center gap-3 px-8 py-4 rounded-full font-medium transition-all duration-300"
              style={{ backgroundColor: "#7B1E3B", color: "#F0E6D2" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#641730")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#7B1E3B")}
            >
              Start Creating <ArrowRight size={18} />
            </Link>
          </motion.div>
          <motion.div {...fadeUp} className="order-1 md:order-2 flex items-center justify-center">
            <motion.div
              animate={{ rotate: [0, 3, -3, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              <GoldenCone size={260} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FULFILLMENT */}
      <section data-testid="fulfillment-section" className="py-20" style={{ backgroundColor: "#C5D0D6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.h2 {...fadeUp} className="font-serif-display text-4xl sm:text-5xl text-center tracking-tight" style={{ color: "#2C2A28" }}>
            Two ways to receive your creation
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            <motion.div {...fadeUp} data-testid="fulfillment-delivery" className="bg-white rounded-3xl p-10 border" style={{ borderColor: "rgba(184,153,104,0.25)" }}>
              <Truck size={36} strokeWidth={1.4} style={{ color: "#B89968" }} />
              <h3 className="font-serif-display text-3xl mt-6" style={{ color: "#2C2A28" }}>Nationwide Delivery</h3>
              <p className="mt-3 text-base leading-relaxed" style={{ color: "#4A4744" }}>
                Shipped across the United States in temperature-controlled packaging — at your door within 48 hours.
              </p>
              <div className="eyebrow mt-6">48 hours · USA-wide</div>
            </motion.div>
            <motion.div {...fadeUp} data-testid="fulfillment-pickup" className="bg-white rounded-3xl p-10 border" style={{ borderColor: "rgba(184,153,104,0.25)" }}>
              <Store size={36} strokeWidth={1.4} style={{ color: "#9CAF88" }} />
              <h3 className="font-serif-display text-3xl mt-6" style={{ color: "#2C2A28" }}>Pickup at Santa Chiara</h3>
              <p className="mt-3 text-base leading-relaxed" style={{ color: "#4A4744" }}>
                Long Island City or Brooklyn — collect your gelato from one of our New York ateliers.
              </p>
              <div className="eyebrow mt-6">LIC · Brooklyn</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta-section" className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Sparkles size={28} strokeWidth={1.4} className="mx-auto mb-6" style={{ color: "#B89968" }} />
          <h2 className="font-serif-display text-4xl sm:text-6xl tracking-tight" style={{ color: "#2C2A28" }}>
            Ready to invent your flavor?
          </h2>
          <Link
            to="/create"
            data-testid="footer-create-cta"
            className="mt-10 inline-flex items-center gap-3 px-10 py-5 rounded-full text-lg font-medium transition-all duration-300"
            style={{ backgroundColor: "#2C2A28", color: "#F0E6D2" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#B89968")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2C2A28")}
          >
            Create Your Gelato <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <ContactForm />

      <Footer />
    </div>
  );
}
